/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { createPortal } from "react-dom";
import { EditIcons } from "./EditIcons";

interface TooltipContainerProps {
  onClose: () => void;
  children: React.ReactNode;

  // NYTT: elementet vi vill aligna mot (t.ex. wrapper i cellen)
  anchorRef: React.RefObject<HTMLElement>;

  // valfritt
  width?: number;
  offset?: number;
}

export const ToolTipContainer = ({
  onClose,
  children,
  anchorRef,
  width = 245,
  offset = 8,
}: TooltipContainerProps) => {
  const toolTipRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{
    top: number;
    left: number;
    placement: "bottom" | "top";
  } | null>(null);

  const updatePosition = React.useCallback(() => {
    const anchorEl = anchorRef.current;
    if (!anchorEl) return;

    const rect = anchorEl.getBoundingClientRect(); // viewport coords
    const margin = 8;

    // centered on anchor
    let left = rect.left + rect.width / 2;
    const minLeft = margin + width / 2;
    const maxLeft = window.innerWidth - margin - width / 2;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // default: under
    let placement: "bottom" | "top" = "bottom";
    let top = rect.bottom + offset;

    // flip if it would go outside viewport bottom
    const approxH = toolTipRef.current?.offsetHeight ?? 140;
    if (top + approxH + margin > window.innerHeight) {
      placement = "top";
      top = rect.top - offset;
    }

    setPos({ top, left, placement });
  }, [anchorRef, width, offset]);

  React.useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, children]);

  React.useEffect(() => {
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true); // viktigt för scroll i containers
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        toolTipRef.current &&
        !toolTipRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  if (!pos) return null;

  const node = (
    <div
      ref={toolTipRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform:
          pos.placement === "bottom"
            ? "translate(-50%, 0)"
            : "translate(-50%, -100%)",
        backgroundColor: "rgb(53, 59, 72)",
        color: "white",
        padding: "8px 12px",
        borderRadius: "4px",
        zIndex: 999999,
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        width,
        wordWrap: "break-word",
        whiteSpace: "normal",
        textOverflow: "ellipsis",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: "0",
          height: "0",
          ...(pos.placement === "bottom"
            ? {
                bottom: "100%",
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderBottom: "6px solid rgb(53, 59, 72)",
              }
            : {
                top: "100%",
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid rgb(53, 59, 72)",
              }),
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            borderBottom: "1px solid white",
            display: "flex",
            flexDirection: "column",
            marginBottom: "2px",
          }}
        >
          <div
            onClick={onClose}
            style={{ cursor: "pointer", alignSelf: "flex-end" }}
          >
            <EditIcons.CrossIcon height="20px" width="20px" fill="white" />
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};
